import React, { useState } from 'react';
import { useAdminData } from '../hooks/useAdminData';
import { Button } from '../components/shared/Button';
import { AgendaView } from '../components/admin/AgendaView';
import { EventReportView } from '../components/admin/EventReportView';
import { formatDuration } from '../utils/formatTime';
import type { PlayedGameEntry } from '../types';

interface AdminPageProps {
  onClose: () => void;
  onNavigateToRoom: (roomId: string) => void;
}

export function AdminPage({ onClose, onNavigateToRoom }: AdminPageProps) {
  const {
    roomList,
    allSessions,
    allActivities,
    totalTrackedMs,
    totalTimersCount,
    loading,
    error,
    deleteRoom,
    exportAsJSON,
    exportAsCSV,
  } = useAdminData();

  const [activeTab, setActiveTab] = useState<'agenda' | 'report' | 'rooms' | 'sessions' | 'activity'>('agenda');
  const [filterQuery, setFilterQuery] = useState('');

  // Filtered lists with defensive checks against missing/corrupt properties
  const query = filterQuery.toLowerCase();

  const filteredRooms = roomList.filter(
    (r) =>
      (r.name || '').toLowerCase().includes(query) ||
      (r.code || '').toLowerCase().includes(query) ||
      (r.createdBy || '').toLowerCase().includes(query)
  );

  const filteredSessions = allSessions.filter((s) => {
    const games: PlayedGameEntry[] = Array.isArray(s.playedGames)
      ? s.playedGames
      : s.playedGames
      ? Object.values(s.playedGames as Record<string, PlayedGameEntry>)
      : [];
    const gamesMatch = games.some((g) => (g.game || '').toLowerCase().includes(query));

    return (
      (s.roomName || '').toLowerCase().includes(query) ||
      (s.timerName || '').toLowerCase().includes(query) ||
      (s.stoppedBy || '').toLowerCase().includes(query) ||
      gamesMatch
    );
  });

  const filteredActivities = allActivities.filter(
    (a) =>
      (a.roomName || '').toLowerCase().includes(query) ||
      (a.timerName || '').toLowerCase().includes(query) ||
      (a.user || '').toLowerCase().includes(query) ||
      (a.action || '').toLowerCase().includes(query) ||
      (a.details || '').toLowerCase().includes(query)
  );

  return (
    <div className="min-h-screen bg-namm-bg text-namm-text font-mono flex flex-col print:bg-white print:text-slate-900 print:min-h-0">
      {/* Top Admin Bar */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-namm-bg/90 border-b border-namm-line/40 print:hidden">
        <div className="max-w-7xl mx-auto px-5 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-namm-cyan bg-namm-cyan/10 flex items-center justify-center text-namm-cyan font-bold font-heading">
              🛡️
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold uppercase tracking-wider text-namm-text">
                namm <span className="text-namm-cyan text-xs font-mono ml-2 border border-namm-cyan/30 px-1.5 py-0.5">ADMIN</span>
              </h1>
              <p className="text-[10px] text-namm-muted uppercase tracking-widest">
                Contrôle global & Audit temps réel
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              variant={activeTab === 'report' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setActiveTab('report')}
            >
              📊 Bilan Événement
            </Button>
            <Button variant="secondary" size="sm" onClick={exportAsCSV}>
              📥 Export CSV
            </Button>
            <Button variant="secondary" size="sm" onClick={exportAsJSON}>
              💾 Backup JSON
            </Button>
            <Button variant="danger" size="sm" onClick={onClose}>
              ✕ Quitter Admin
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-5 py-8 print:p-0 print:max-w-none">
        {loading && (
          <div className="py-20 text-center text-namm-muted animate-pulse">
            Chargement des données globales Firebase...
          </div>
        )}

        {error && (
          <div className="p-4 mb-6 border border-namm-danger/40 bg-namm-danger/10 text-namm-danger text-xs">
            Erreur : {error}
          </div>
        )}

        {!loading && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8 print:hidden">
              <div className="p-4 border border-namm-line/30 bg-namm-panel/70">
                <span className="text-[10px] uppercase text-namm-muted tracking-widest block">
                  Salles (Rooms)
                </span>
                <span className="text-2xl font-heading font-bold text-namm-cyan mt-1 block">
                  {roomList.length}
                </span>
              </div>

              <div className="p-4 border border-namm-line/30 bg-namm-panel/70">
                <span className="text-[10px] uppercase text-namm-muted tracking-widest block">
                  Chronomètres
                </span>
                <span className="text-2xl font-heading font-bold text-namm-text mt-1 block">
                  {totalTimersCount}
                </span>
              </div>

              <div className="p-4 border border-namm-line/30 bg-namm-panel/70">
                <span className="text-[10px] uppercase text-namm-muted tracking-widest block">
                  Sessions archivées
                </span>
                <span className="text-2xl font-heading font-bold text-namm-cyan mt-1 block">
                  {allSessions.length}
                </span>
              </div>

              <div className="p-4 border border-namm-line/30 bg-namm-panel/70">
                <span className="text-[10px] uppercase text-namm-muted tracking-widest block">
                  Temps cumulé
                </span>
                <span className="text-xl font-heading font-bold text-green-400 mt-1 block truncate">
                  {formatDuration(totalTrackedMs)}
                </span>
              </div>

              <div className="p-4 border border-namm-line/30 bg-namm-panel/70 col-span-2 md:col-span-1">
                <span className="text-[10px] uppercase text-namm-muted tracking-widest block">
                  Actions journalisées
                </span>
                <span className="text-2xl font-heading font-bold text-purple-400 mt-1 block">
                  {allActivities.length}
                </span>
              </div>
            </div>

            {/* Navigation & Search Bar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-namm-line/25 print:hidden">
              {/* Tabs */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setActiveTab('agenda')}
                  className={`py-2 px-3 text-xs uppercase tracking-wider border-b-2 transition-all ${
                    activeTab === 'agenda'
                      ? 'border-namm-cyan text-namm-cyan font-semibold'
                      : 'border-transparent text-namm-muted hover:text-namm-text'
                  }`}
                >
                  📅 Agenda du jour
                </button>
                <button
                  onClick={() => setActiveTab('report')}
                  className={`py-2 px-3 text-xs uppercase tracking-wider border-b-2 transition-all ${
                    activeTab === 'report'
                      ? 'border-namm-cyan text-namm-cyan font-semibold'
                      : 'border-transparent text-namm-muted hover:text-namm-text'
                  }`}
                >
                  📊 Bilan d'événement
                </button>
                <button
                  onClick={() => setActiveTab('rooms')}
                  className={`py-2 px-3 text-xs uppercase tracking-wider border-b-2 transition-all ${
                    activeTab === 'rooms'
                      ? 'border-namm-cyan text-namm-cyan font-semibold'
                      : 'border-transparent text-namm-muted hover:text-namm-text'
                  }`}
                >
                  🗂️ Salles ({filteredRooms.length})
                </button>
                <button
                  onClick={() => setActiveTab('sessions')}
                  className={`py-2 px-3 text-xs uppercase tracking-wider border-b-2 transition-all ${
                    activeTab === 'sessions'
                      ? 'border-namm-cyan text-namm-cyan font-semibold'
                      : 'border-transparent text-namm-muted hover:text-namm-text'
                  }`}
                >
                  ⏱ Sessions ({filteredSessions.length})
                </button>
                <button
                  onClick={() => setActiveTab('activity')}
                  className={`py-2 px-3 text-xs uppercase tracking-wider border-b-2 transition-all ${
                    activeTab === 'activity'
                      ? 'border-namm-cyan text-namm-cyan font-semibold'
                      : 'border-transparent text-namm-muted hover:text-namm-text'
                  }`}
                >
                  📜 Flux d'audit ({filteredActivities.length})
                </button>
              </div>

              {/* Search input */}
              <div className="w-full md:w-64">
                <input
                  type="text"
                  placeholder="Rechercher (salle, user, jeu)..."
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  className="w-full bg-namm-panel-soft border border-namm-line/60 px-3 py-1.5 text-xs text-namm-text outline-none focus:border-namm-cyan placeholder:text-namm-muted/50"
                />
              </div>
            </div>

            {/* Tab 0: Agenda */}
            {activeTab === 'agenda' && (
              <AgendaView sessions={allSessions} />
            )}

            {/* Tab: Bilan & Synthèse d'Événement */}
            {activeTab === 'report' && (
              <EventReportView sessions={allSessions} />
            )}

            {/* Tab 1: Rooms */}
            {activeTab === 'rooms' && (
              <div className="space-y-3">
                {filteredRooms.length === 0 ? (
                  <div className="text-center py-12 text-namm-muted text-xs">
                    Aucune salle trouvée.
                  </div>
                ) : (
                  filteredRooms.map((room) => {
                    const timersCount = room.timers ? Object.keys(room.timers).length : 0;
                    const sessionCount = room.history ? Object.keys(room.history).length : 0;
                    const activityCount = room.activityLog ? Object.keys(room.activityLog).length : 0;
                    const createdDate = room.createdAt
                      ? new Date(room.createdAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Date inconnue';

                    return (
                      <div
                        key={room.id}
                        className="p-4 border border-namm-line/30 bg-namm-panel/60 flex flex-col md:flex-row md:items-center justify-between gap-4"
                      >
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="font-heading font-bold text-base uppercase text-namm-text">
                              {room.name || 'Salle sans nom'}
                            </h3>
                            <span className="text-xs text-namm-cyan font-mono border border-namm-cyan/40 px-1.5 py-0.5">
                              {room.code || (room.id ? room.id.slice(0, 6).toUpperCase() : '---')}
                            </span>
                          </div>
                          <div className="text-[11px] text-namm-muted mt-1">
                            Créée par <span className="text-namm-text">{room.createdBy || 'Inconnu'}</span> le {createdDate}
                            <span className="mx-2">·</span>
                            <span>{timersCount} chrono{timersCount > 1 ? 's' : ''}</span>
                            <span className="mx-2">·</span>
                            <span>{sessionCount} session{sessionCount > 1 ? 's' : ''}</span>
                            <span className="mx-2">·</span>
                            <span>{activityCount} action{activityCount > 1 ? 's' : ''}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onNavigateToRoom(room.id)}
                          >
                            Rejoindre ➔
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => deleteRoom(room.id)}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Tab 2: Sessions */}
            {activeTab === 'sessions' && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border border-namm-line/30">
                  <thead className="bg-namm-panel-soft text-namm-muted border-b border-namm-line/30 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="py-2.5 px-3">Salle</th>
                      <th className="py-2.5 px-3">Chronomètre & Jeux</th>
                      <th className="py-2.5 px-3">Type</th>
                      <th className="py-2.5 px-3">Durée</th>
                      <th className="py-2.5 px-3">Par</th>
                      <th className="py-2.5 px-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-namm-line/20">
                    {filteredSessions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-namm-muted">
                          Aucune session trouvée.
                        </td>
                      </tr>
                    ) : (
                      filteredSessions.map((s) => {
                        const games: PlayedGameEntry[] = Array.isArray(s.playedGames)
                          ? s.playedGames
                          : s.playedGames
                          ? Object.values(s.playedGames as Record<string, PlayedGameEntry>)
                          : [];

                        return (
                          <tr key={s.id} className="hover:bg-namm-panel-soft/40 transition-colors">
                            <td className="py-2.5 px-3 font-semibold text-namm-text">
                              {s.roomName || 'Salle sans nom'}
                            </td>
                            <td className="py-2.5 px-3 text-namm-cyan">
                              <div className="font-semibold">"{s.timerName || 'Sans titre'}"</div>
                              {games.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {games.map((g, idx) => (
                                    <span
                                      key={idx}
                                      className="text-[9px] bg-namm-cyan/10 border border-namm-cyan/30 text-namm-cyan px-1.5 py-0.5 rounded font-mono"
                                    >
                                      🎮 {g.game}
                                      {g.durationMs && (
                                        <span className="text-namm-muted ml-1 font-normal">
                                          ({formatDuration(g.durationMs)})
                                        </span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-namm-muted uppercase text-[10px]">
                              {s.type || '-'}
                            </td>
                            <td className="py-2.5 px-3 font-semibold text-green-400 tabular-nums">
                              {formatDuration(s.totalDurationMs || 0)}
                            </td>
                            <td className="py-2.5 px-3 text-namm-text">
                              {s.stoppedBy || 'Anonyme'}
                            </td>
                            <td className="py-2.5 px-3 text-namm-muted text-[11px] tabular-nums">
                              {s.endedAt
                                ? new Date(s.endedAt).toLocaleDateString('fr-FR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '-'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Tab 3: Global Activity */}
            {activeTab === 'activity' && (
              <div className="space-y-1.5 max-h-[600px] overflow-y-auto">
                {filteredActivities.length === 0 ? (
                  <div className="text-center py-12 text-namm-muted text-xs">
                    Aucune action trouvée.
                  </div>
                ) : (
                  filteredActivities.map((act) => {
                    const timeStr = act.timestamp
                      ? new Date(act.timestamp).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })
                      : '-';

                    return (
                      <div
                        key={act.id}
                        className="py-2 px-3 bg-namm-panel-soft/50 border border-namm-line/20 flex items-center justify-between gap-3 text-xs"
                      >
                        <div className="flex items-center gap-2.5 truncate">
                          <span className="text-[10px] text-namm-muted border border-namm-line/30 px-1 py-0.5 uppercase tracking-wider shrink-0">
                            {act.roomName || 'Salle sans nom'}
                          </span>
                          <span
                            className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-namm-bg shrink-0"
                            style={{ backgroundColor: act.userColor || '#00e5ff' }}
                          >
                            {(act.user || 'A').charAt(0).toUpperCase()}
                          </span>
                          <span className="font-semibold text-namm-text shrink-0">
                            {act.user || 'Anonyme'}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-namm-cyan/40 bg-namm-cyan/10 text-namm-cyan shrink-0">
                            {act.action || 'action'}
                          </span>
                          <span className="text-namm-cyan truncate">
                            "{act.timerName || 'Chrono'}"
                          </span>
                          {act.details && (
                            <span className="text-namm-muted text-[11px] truncate">
                              ({act.details})
                            </span>
                          )}
                        </div>
                        <span className="text-namm-muted text-[11px] tabular-nums shrink-0">
                          {timeStr}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
